<template>
  <v-container fluid class="dashboard-container pa-0 pb-12">
    <!-- Header: Identity & Actions -->
    <v-sheet color="white" class="pa-6 pa-md-8 mb-6 border-bottom shadow-xs">
      <v-row align="center" justify="space-between">
        <v-col cols="12" lg="6">
          <div class="d-flex align-center">
            <v-avatar size="52" color="primary lighten-5" class="mr-4 rounded-xl">
              <v-icon color="primary">fas fa-flag-checkered</v-icon>
            </v-avatar>
            <div>
              <div class="d-flex align-center flex-wrap" style="gap: 8px">
                <h1 class="text-h5 font-weight-black primary--text tracking-tight mb-0">
                  Dashboard de Postcosteo
                </h1>
                <v-chip v-if="asOf" x-small label color="blue-grey lighten-5" class="blue-grey--text text--darken-3 font-weight-medium">
                  <v-icon left x-small>fas fa-clock</v-icon>
                  Actualizado {{ formattedAsOf }}
                </v-chip>
              </div>
              <p class="body-2 grey--text text--darken-1 font-weight-medium mb-0 mt-1">
                Rentabilidad real de OTs cerradas · Período:
                <span class="font-weight-bold grey--text text--darken-3">{{ periodLabel }}</span>
                · <span class="font-weight-bold primary--text">{{ selectedLineName }}</span>
              </p>
            </div>
          </div>
        </v-col>

        <!-- Actions & Date Mode -->
        <v-col cols="12" lg="6" class="d-flex align-center justify-lg-end flex-wrap" style="gap: 10px">
          <!-- Date Mode Toggle -->
          <div class="date-mode-box px-2 py-1 rounded-xl d-flex align-center">
            <span class="caption font-weight-bold grey--text text--darken-2 mr-2 ml-1 text-uppercase" style="font-size: 11px;">Fecha:</span>
            <v-btn-toggle v-model="dateMode" mandatory dense class="mode-toggle rounded-lg">
              <v-btn value="completed" small class="text-none font-weight-bold px-3">
                <v-icon left x-small color="success">fas fa-check-double</v-icon>
                Cierre
              </v-btn>
              <v-btn value="created" small class="text-none font-weight-bold px-3">
                <v-icon left x-small color="primary">fas fa-plus</v-icon>
                Creación
              </v-btn>
            </v-btn-toggle>
          </div>

          <export-post-cost :filter="filter" :options="options" :disabled="loading" :search="search" :date-mode="dateMode" />

          <v-btn
            outlined
            color="primary"
            class="rounded-xl px-4 font-weight-bold text-none shadow-xs"
            :loading="downloadingPdf"
            :disabled="loadingDashboard || loading"
            @click="downloadExecutivePdf"
          >
            <v-icon left small color="error">fas fa-file-pdf</v-icon>
            Exportar PDF
          </v-btn>

          <v-btn
            color="primary"
            depressed
            class="rounded-xl px-5 font-weight-bold text-none shadow-xs"
            :loading="loadingDashboard || loading"
            @click="loadAll"
          >
            <v-icon left small>fas fa-sync-alt</v-icon>
            Actualizar
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>

    <div class="px-6 px-md-8">
      <!-- Time Control & Filters Card -->
      <v-card flat outlined class="filter-card pa-4 mb-6 shadow-xs">
        <!-- Row 1: Quick Presets & Date Inputs -->
        <v-row align="center" dense class="mb-3">
          <v-col cols="12" md="7">
            <div class="d-flex align-center flex-wrap" style="gap: 6px">
              <span class="caption font-weight-bold grey--text text--darken-2 mr-2 text-uppercase">
                Rango rápido:
              </span>
              <v-btn
                v-for="preset in periodOptions"
                :key="preset.value"
                x-small
                depressed
                :color="period === preset.value ? 'primary' : 'grey lighten-4'"
                :class="period === preset.value ? 'white--text font-weight-bold' : 'grey--text text--darken-3 font-weight-medium'"
                class="rounded-lg text-none px-3 py-2"
                @click="selectPeriod(preset.value)"
              >
                {{ preset.text }}
              </v-btn>
            </div>
          </v-col>

          <v-col cols="12" md="5" class="d-flex align-center justify-md-end flex-wrap" style="gap: 8px">
            <div class="d-flex align-center" style="gap: 8px">
              <v-text-field
                v-model="filter.created_after"
                label="Desde"
                outlined
                dense
                hide-details
                type="date"
                class="compact-date-input"
                @change="onCustomDates"
              />
              <span class="grey--text">—</span>
              <v-text-field
                v-model="filter.created_before"
                label="Hasta"
                outlined
                dense
                hide-details
                type="date"
                class="compact-date-input"
                @change="onCustomDates"
              />
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>

        <!-- Row 2: Secondary Filters (Client, Seller, Type) -->
        <v-row align="center" dense>
          <v-col cols="12" sm="6" md="4">
            <customer-complete2
              v-model="filter.customer"
              :disabled="loadingDashboard"
              @change="loadAll"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="filter.seller"
              :items="sellers"
              item-text="email"
              item-value="id"
              placeholder="Todos los ejecutivos"
              label="Ejecutivo Comercial"
              outlined
              dense
              hide-details
              clearable
              class="compact-select"
              prepend-inner-icon="fas fa-user-tie"
              @change="loadAll"
            >
              <template v-slot:selection="{ item }">
                <span class="body-2 font-weight-bold">{{ item.first_name }} {{ item.last_name }}</span>
              </template>
              <template v-slot:item="{ item }">
                <div class="pa-1">
                  <div class="body-2 font-weight-black">{{ item.first_name }} {{ item.last_name }}</div>
                  <div class="caption grey--text">{{ item.email }}</div>
                </div>
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="filter.reposicion"
              :items="reposicionOptions"
              item-text="text"
              item-value="value"
              placeholder="Todas las órdenes"
              label="Tipo de OT"
              outlined
              dense
              hide-details
              clearable
              class="compact-select"
              @change="loadAll"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="line"
              :items="businessLineOptions"
              item-value="id"
              item-text="name"
              label="Línea de negocio"
              prepend-inner-icon="fas fa-layer-group"
              outlined
              dense
              hide-details
              class="compact-select"
            />
          </v-col>
        </v-row>
      </v-card>

      <!-- Error State -->
      <v-alert
        v-if="error"
        dense
        outlined
        type="error"
        class="mb-6 rounded-xl bg-white shadow-xs"
      >
        <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px">
          <span>{{ error }}</span>
          <v-btn small color="error" depressed class="rounded-lg text-none font-weight-bold" @click="loadAll">
            <v-icon left small>fas fa-redo</v-icon>
            Reintentar
          </v-btn>
        </div>
      </v-alert>

      <!-- Loading Skeletons -->
      <v-row v-if="loadingDashboard && !dash.count" dense class="mb-6">
        <v-col v-for="i in 4" :key="'sk-' + i" cols="12" sm="6" md="3">
          <v-skeleton-loader type="card" height="136" class="rounded-xl shadow-xs" />
        </v-col>
      </v-row>

      <template v-else>
        <!-- Row 1: Hero KPIs (4 Master Cards) -->
        <v-row dense class="mb-6">
          <!-- KPI 1: Venta Cerrada -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100 is-primary">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Venta Cerrada</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Monto neto de venta facturada/cerrada de las Órdenes de Trabajo del período.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value primary--text text--darken-3">
                  {{ dash.sales | moneda }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">
                      {{ dash.count || 0 }} OT finalizadas
                    </span>
                    <span class="caption font-weight-bold primary--text">
                      {{ dateMode === 'completed' ? 'Por cierre' : 'Por creación' }}
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- KPI 2: Costo Real -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Costo Real Total</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Costo total incurrido en planta (procesos, reprocesos, materiales, bodega y compras).</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value grey--text text--darken-4">
                  {{ dash.real | moneda }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="caption font-weight-bold grey--text text--darken-1">
                    Teórico cotizado: {{ dash.estimated | moneda }}
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- KPI 3: Margen Real -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Margen Real</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Margen de rentabilidad real promedio ponderado (1 - Costo Real / Venta) vs. Margen cotizado.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value" :class="marginClass(dash.real_margin)">
                  {{ (dash.real_margin || 0) | percent(1) }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">
                      Teórico {{ (dash.estimated_margin || 0) | percent(1) }}
                    </span>
                    <span
                      class="caption font-weight-black"
                      :class="(dash.real_margin || 0) >= (dash.estimated_margin || 0) ? 'teal--text text--darken-2' : 'error--text'"
                    >
                      {{ ((dash.real_margin - dash.estimated_margin) * 100).toFixed(1) }} pp
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- KPI 4: Resultado Neto -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Resultado Operacional</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Ganancia neta (Venta - Costo Real) y desvío total frente a la cotización presupuestada.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value" :class="(dash.profit || 0) >= 0 ? 'teal--text text--darken-3' : 'error--text'">
                  {{ dash.profit | moneda }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">
                      Desvío vs teórico:
                    </span>
                    <span class="caption font-weight-black" :class="(dash.variance || 0) <= 0 ? 'teal--text text--darken-2' : 'error--text'">
                      {{ dash.variance | moneda }}
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Executive Insight Card -->
        <v-row v-if="dash.insight" class="mb-6">
          <v-col cols="12">
            <v-card color="#0F172A" dark flat class="rounded-xl pa-5 elevation-1">
              <div class="d-flex align-center mb-2 flex-wrap" style="gap: 8px">
                <v-chip color="teal darken-1" x-small class="font-weight-black text-uppercase tracking-widest px-2">
                  Lectura del período
                </v-chip>
                <div class="text-subtitle-1 font-weight-black yellow--text text--accent-2">{{ dash.insight.title }}</div>
              </div>
              <div class="text-body-2 grey--text text--lighten-2 mb-2">{{ dash.insight.body }}</div>
              <v-chip v-if="dash.insight.highlight" color="yellow darken-1" text-color="black" small class="font-weight-black text-uppercase px-2">
                {{ dash.insight.highlight }}
              </v-chip>
            </v-card>
          </v-col>
        </v-row>

        <!-- Postcosteo por Línea de Negocio -->
        <v-card flat class="panel-card mb-6 shadow-xs">
          <div class="d-flex align-center justify-space-between mb-1 flex-wrap" style="gap: 8px">
            <h2 class="panel-title mb-0">Por Línea de Negocio</h2>
            <span class="caption grey--text font-weight-bold">Clic en una fila para filtrar el dashboard</span>
          </div>
          <div class="panel-sub mb-4">
            Procesos y materiales se asignan a la línea de cada producto; bodega, adicionales y compras de la OT se prorratean según su participación en la venta.
          </div>
          <div v-if="!lineBreakdown.length" class="empty-mini py-6 text-center">Sin OTs cerradas en este filtro</div>
          <v-simple-table v-else dense class="line-table">
            <thead>
              <tr>
                <th>Línea</th>
                <th class="text-end">OTs</th>
                <th class="text-end">Venta</th>
                <th class="text-end">Costo teórico</th>
                <th class="text-end">Costo real</th>
                <th class="text-end">Resultado</th>
                <th class="text-end">Margen real</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in lineBreakdown"
                :key="'l-' + row.id"
                class="line-row"
                :class="{ 'is-selected-line': String(line) === String(row.id) }"
                @click="toggleLine(row.id)"
              >
                <td class="font-weight-bold">{{ row.name }}</td>
                <td class="text-end">{{ row.count }}</td>
                <td class="text-end font-weight-black">{{ row.sales | moneda }}</td>
                <td class="text-end">{{ row.estimated | moneda }}</td>
                <td class="text-end">{{ row.real | moneda }}</td>
                <td class="text-end font-weight-bold" :class="row.profit >= 0 ? 'teal--text text--darken-3' : 'error--text'">{{ row.profit | moneda }}</td>
                <td class="text-end font-weight-black" :class="marginClass(row.margin)">{{ row.margin | percent(1) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>

        <!-- Row 2: Distribución de Salud de Margen -->
        <v-card flat class="panel-card mb-6 shadow-xs">
          <div class="d-flex align-center justify-space-between mb-3 flex-wrap" style="gap: 8px">
            <div>
              <div class="d-flex align-center">
                <h2 class="panel-title mb-0">Distribución de Salud de Margen</h2>
                <v-tooltip bottom max-width="320">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" small color="grey" class="ml-2">fas fa-info-circle</v-icon>
                  </template>
                  <span>Segmentación de las OTs según el margen real ejecutado en planta frente al precio de venta.</span>
                </v-tooltip>
              </div>
              <div class="panel-sub">Rentabilidad real acumulada de las OTs del período</div>
            </div>
            <div class="caption grey--text text--darken-2 font-weight-medium">
              Venta evaluada: <span class="font-weight-black grey--text text--darken-4">{{ totalMarginSales | moneda }}</span>
            </div>
          </div>

          <!-- Stacked Segmented Bar -->
          <div class="margin-stacked-track mb-4">
            <div
              v-if="marginHealthyPct > 0"
              class="stacked-segment is-healthy"
              :style="{ width: marginHealthyPct + '%' }"
            >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="segment-label">{{ marginHealthyPct.toFixed(1) }}%</span>
                </template>
                <span>Sano (≥30%): {{ (buckets.healthy && buckets.healthy.sales) | moneda }} ({{ (buckets.healthy && buckets.healthy.count) || 0 }} OT)</span>
              </v-tooltip>
            </div>
            <div
              v-if="marginOkPct > 0"
              class="stacked-segment is-ok"
              :style="{ width: marginOkPct + '%' }"
            >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="segment-label">{{ marginOkPct.toFixed(1) }}%</span>
                </template>
                <span>Aceptable (15–30%): {{ (buckets.ok && buckets.ok.sales) | moneda }} ({{ (buckets.ok && buckets.ok.count) || 0 }} OT)</span>
              </v-tooltip>
            </div>
            <div
              v-if="marginTightPct > 0"
              class="stacked-segment is-tight"
              :style="{ width: marginTightPct + '%' }"
            >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="segment-label">{{ marginTightPct.toFixed(1) }}%</span>
                </template>
                <span>Ajustado (0–15%): {{ (buckets.tight && buckets.tight.sales) | moneda }} ({{ (buckets.tight && buckets.tight.count) || 0 }} OT)</span>
              </v-tooltip>
            </div>
            <div
              v-if="marginLossPct > 0"
              class="stacked-segment is-loss"
              :style="{ width: marginLossPct + '%' }"
            >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="segment-label">{{ marginLossPct.toFixed(1) }}%</span>
                </template>
                <span>Pérdida (&lt;0%): {{ (buckets.loss && buckets.loss.sales) | moneda }} ({{ (buckets.loss && buckets.loss.count) || 0 }} OT)</span>
              </v-tooltip>
            </div>
          </div>

          <!-- 4 Interactive Tier Cards -->
          <v-row dense>
            <v-col cols="12" sm="6" md="3">
              <div class="margin-tier-btn is-healthy">
                <div class="d-flex align-center justify-space-between">
                  <span class="tier-name">Sano ≥ 30%</span>
                  <span class="tier-count">{{ (buckets.healthy && buckets.healthy.count) || 0 }} OT</span>
                </div>
                <div class="tier-sales">{{ (buckets.healthy && buckets.healthy.sales) | moneda }}</div>
                <div class="tier-share">{{ marginHealthyPct.toFixed(1) }}% de la venta</div>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="margin-tier-btn is-ok">
                <div class="d-flex align-center justify-space-between">
                  <span class="tier-name">Aceptable 15–30%</span>
                  <span class="tier-count">{{ (buckets.ok && buckets.ok.count) || 0 }} OT</span>
                </div>
                <div class="tier-sales">{{ (buckets.ok && buckets.ok.sales) | moneda }}</div>
                <div class="tier-share">{{ marginOkPct.toFixed(1) }}% de la venta</div>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="margin-tier-btn is-tight">
                <div class="d-flex align-center justify-space-between">
                  <span class="tier-name">Ajustado 0–15%</span>
                  <span class="tier-count">{{ (buckets.tight && buckets.tight.count) || 0 }} OT</span>
                </div>
                <div class="tier-sales">{{ (buckets.tight && buckets.tight.sales) | moneda }}</div>
                <div class="tier-share">{{ marginTightPct.toFixed(1) }}% de la venta</div>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="margin-tier-btn is-loss">
                <div class="d-flex align-center justify-space-between">
                  <span class="tier-name">Pérdida &lt; 0%</span>
                  <span class="tier-count">{{ (buckets.loss && buckets.loss.count) || 0 }} OT</span>
                </div>
                <div class="tier-sales">{{ (buckets.loss && buckets.loss.sales) | moneda }}</div>
                <div class="tier-share">
                  {{ marginLossPct.toFixed(1) }}% de la venta · {{ dash.overrun_ots || 0 }} desvío &gt;10%
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card>

        <!-- Row 3: Charts (Timeline + Cost Structure) -->
        <v-row dense class="mb-6">
          <v-col cols="12" lg="8">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="legend-row mb-3">
                <div>
                  <div class="panel-title">Venta vs Costo en el Tiempo</div>
                  <div class="panel-sub">Evolución mensual de venta cotizada, costo teórico y costo real ejecutado</div>
                </div>
                <div class="legend">
                  <span><i class="swatch sold"></i> Venta</span>
                  <span><i class="swatch quoted"></i> Costo Teórico</span>
                  <span><i class="swatch real"></i> Costo Real</span>
                </div>
              </div>
              <div v-if="!(dash.monthly && dash.monthly.length)" class="empty-mini py-12 text-center">
                <v-icon color="grey lighten-1" class="mb-2" large>fas fa-chart-area</v-icon>
                <div>No hay meses con OTs cerradas en este filtro</div>
              </div>
              <div v-else style="height: 310px">
                <line-chart ref="salesLineChart" :chart-data="lineData" :options="lineOptions" :height="310" />
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" lg="4">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="panel-title mb-1">Estructura del Costo Real</div>
              <div class="panel-sub mb-4">Desglose de gastos acumulados en planta</div>
              <div v-if="!pieHasData" class="empty-mini py-12 text-center">
                <v-icon color="grey lighten-1" class="mb-2" large>fas fa-chart-pie</v-icon>
                <div>Sin desglose de costos disponible</div>
              </div>
              <div v-else style="height: 250px">
                <doughnut-chart :chart-data="pieData" :options="pieOptions" />
              </div>
              <div class="d-flex align-center justify-space-between mt-3 pt-3 border-top" v-if="dash.reprocess_ots">
                <span class="caption grey--text font-weight-bold">OTs con reproceso:</span>
                <v-chip x-small color="amber lighten-5" class="amber--text text--darken-4 font-weight-bold">
                  {{ dash.reprocess_ots }} OT afectadas
                </v-chip>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Row 4: Rankings (Clients & Sellers) -->
        <v-row dense class="mb-6">
          <v-col cols="12" md="6">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Clientes por Venta Cerrada</div>
                <span class="caption grey--text font-weight-bold">{{ (dash.customers || []).length }} principales</span>
              </div>
              <div class="panel-sub mb-4">Monto facturado y margen real por cliente</div>
              <div v-if="!(dash.customers && dash.customers.length)" class="empty-mini py-8 text-center">
                Sin datos de clientes para este período
              </div>
              <div v-for="row in dash.customers" :key="'c-' + row.name" class="rank-row">
                <div class="d-flex align-center justify-space-between">
                  <div class="rank-name font-weight-bold text-truncate" style="max-width: 65%">{{ row.name }}</div>
                  <div class="rank-val font-weight-black">{{ row.sales | moneda }}</div>
                </div>
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="rank-meta">{{ row.count }} OT · Margen real: <span class="font-weight-bold" :class="marginClass(row.margin)">{{ row.margin | percent(1) }}</span></div>
                  <span class="caption grey--text">Costo: {{ row.real | moneda }}</span>
                </div>
                <div class="rank-track">
                  <div class="rank-fill is-teal" :style="{ width: rankWidth(row.sales, maxCustomerSales) }"></div>
                </div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Ejecutivos Comerciales</div>
                <span class="caption grey--text font-weight-bold">{{ (dash.sellers || []).length }} activos</span>
              </div>
              <div class="panel-sub mb-4">Venta cerrada y rentabilidad según vendedor</div>
              <div v-if="!(dash.sellers && dash.sellers.length)" class="empty-mini py-8 text-center">
                Sin datos de ejecutivos para este período
              </div>
              <div v-for="row in dash.sellers" :key="'s-' + row.name" class="rank-row">
                <div class="d-flex align-center justify-space-between">
                  <div class="rank-name font-weight-bold text-truncate" style="max-width: 65%">{{ row.name }}</div>
                  <div class="rank-val font-weight-black">{{ row.sales | moneda }}</div>
                </div>
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="rank-meta">{{ row.count }} OT · Margen real: <span class="font-weight-bold" :class="marginClass(row.margin)">{{ row.margin | percent(1) }}</span></div>
                  <span class="caption grey--text">Costo: {{ row.real | moneda }}</span>
                </div>
                <div class="rank-track">
                  <div class="rank-fill" :style="{ width: rankWidth(row.sales, maxSellerSales) }"></div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Row 5: Rankings (Losses & Best Margins) -->
        <v-row dense class="mb-6">
          <v-col cols="12" md="6">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">OTs con Mayor Erosión de Margen</div>
                <span class="caption error--text font-weight-bold" v-if="dash.losses && dash.losses.length">
                  {{ dash.losses.length }} en pérdida
                </span>
              </div>
              <div class="panel-sub mb-4">Órdenes con peor margen real · Haz clic para abrir desglose</div>
              <div v-if="!(dash.worst && dash.worst.length)" class="empty-mini py-8 text-center">
                Sin órdenes con margen negativo en este período
              </div>
              <button
                v-for="row in dash.worst"
                :key="'w-' + row.id"
                type="button"
                class="ot-row"
                @click="openDetail(row)"
              >
                <div>
                  <div class="d-flex align-center">
                    <span class="ot-id mr-2">OT #{{ row.id }}</span>
                    <span class="ot-cust text-truncate">{{ row.customer || 'Sin cliente' }}</span>
                  </div>
                  <div class="ot-meta text-truncate">{{ row.name || 'Sin referencia' }}</div>
                </div>
                <div class="ot-nums">
                  <div class="font-weight-black" :class="marginClass(row.margin)">
                    {{ row.margin | percent(1) }}
                  </div>
                  <div class="caption grey--text">{{ row.real | moneda }} real</div>
                </div>
              </button>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Mejor Ejecución de Rentabilidad</div>
                <span class="caption success--text font-weight-bold" v-if="dash.best && dash.best.length">
                  Mayor utilidad
                </span>
              </div>
              <div class="panel-sub mb-4">Órdenes con mayor margen real obtenido sobre la venta</div>
              <div v-if="!(dash.best && dash.best.length)" class="empty-mini py-8 text-center">
                Sin órdenes para rankear
              </div>
              <button
                v-for="row in dash.best"
                :key="'b-' + row.id"
                type="button"
                class="ot-row is-best"
                @click="openDetail(row)"
              >
                <div>
                  <div class="d-flex align-center">
                    <span class="ot-id mr-2">OT #{{ row.id }}</span>
                    <span class="ot-cust text-truncate">{{ row.customer || 'Sin cliente' }}</span>
                  </div>
                  <div class="ot-meta text-truncate">{{ row.name || 'Sin referencia' }}</div>
                </div>
                <div class="ot-nums">
                  <div class="font-weight-black teal--text text--darken-3">
                    {{ row.margin | percent(1) }}
                  </div>
                  <div class="caption grey--text">{{ row.profit | moneda }} utilidad</div>
                </div>
              </button>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <!-- Row 6: Master Data Table -->
      <v-card flat outlined class="modern-card overflow-hidden shadow-xs mb-8">
        <v-data-table
          :headers="headers"
          :items="workOrders"
          :options.sync="options"
          :server-items-length="totalWorkOrders"
          :loading="loading"
          class="modern-table"
          @update:options="fetch"
          :footer-props="{
            showFirstLastPage: true,
            itemsPerPageOptions: [10, 20, 50, 100],
          }"
        >
          <template v-slot:top>
            <v-sheet color="white" class="px-6 py-4 d-flex align-center border-bottom flex-wrap" style="gap: 12px">
              <div>
                <span class="font-weight-black grey--text text--darken-4 text-subtitle-2">
                  Detalle de Órdenes de Trabajo Finalizadas
                </span>
                <span v-if="line" class="caption grey--text ml-1">
                  · OTs que incluyen {{ selectedLineName }} (montos de la OT completa)
                </span>
                <v-chip x-small label color="grey lighten-4" class="ml-2 grey--text text--darken-3 font-weight-bold">
                  {{ totalWorkOrders }} registros
                </v-chip>
              </div>
              <v-spacer></v-spacer>
              <v-text-field
                v-model="search"
                @keyup="searchTimeOut"
                placeholder="Buscar por OT, cliente o proyecto..."
                prepend-inner-icon="fas fa-search"
                outlined
                dense
                hide-details
                clearable
                :disabled="loading"
                class="modern-search-field"
              ></v-text-field>
            </v-sheet>
          </template>

          <template v-slot:item.id="{ item }">
            <show-work-order :work_order_id="item.id">
              <div class="d-flex align-center py-2">
                <v-avatar size="32" color="primary lighten-5" class="mr-2 rounded-lg">
                  <span class="caption font-weight-black primary--text">OT</span>
                </v-avatar>
                <span class="body-2 font-weight-black primary--text">#{{ item.id }}</span>
              </div>
            </show-work-order>
          </template>

          <template v-slot:item.created_at="{ item }">
            <div class="d-flex flex-column py-2">
              <span class="caption grey--text font-weight-medium d-flex align-center mb-1">
                <v-icon x-small class="mr-1" color="grey lighten-1">fas fa-plus</v-icon>
                {{ item.created_at | moment("DD/MM/YYYY") }}
              </span>
              <span class="caption teal--text text--darken-3 font-weight-bold d-flex align-center" v-if="item.completed_at">
                <v-icon x-small class="mr-1" color="teal darken-2">fas fa-check-double</v-icon>
                {{ item.completed_at | moment("DD/MM/YYYY") }}
              </span>
            </div>
          </template>

          <template v-slot:item.sales_note__name="{ item }">
            <div class="d-flex flex-column py-2">
              <span class="body-2 font-weight-bold grey--text text--darken-4 lh-1-2 mb-1">{{ item.name || 'Sin referencia' }}</span>
              <div class="d-flex align-center" v-if="item.sales_note">
                <v-icon x-small color="grey lighten-1" class="mr-1">fas fa-file-invoice</v-icon>
                <span class="caption grey--text font-weight-bold">CN #{{ item.sales_note }}</span>
              </div>
            </div>
          </template>

          <template v-slot:item.sales_note__customer__name="{ item }">
            <div class="d-flex flex-column py-2">
              <span class="caption font-weight-black text-uppercase grey--text text--darken-3 mb-1">{{ item.customer_name }}</span>
              <div class="d-flex align-center">
                <v-avatar size="18" color="grey lighten-4" class="mr-1 text-center">
                  <v-icon color="grey darken-1" style="font-size: 10px">fas fa-user-tie</v-icon>
                </v-avatar>
                <span class="caption grey--text text--darken-1 font-weight-bold">{{ item.seller_full_name }}</span>
              </div>
            </div>
          </template>

          <template v-slot:item.total_sale="{ item }">
            <span class="body-1 font-weight-black primary--text tracking-tight">
              {{ (item.total_sale || (item.real_price && item.real_price.sales_total) || 0) | moneda }}
            </span>
          </template>

          <template v-slot:item.estimated_cost="{ item }">
            <div class="d-flex flex-column align-end py-2">
              <span class="body-2 font-weight-bold grey--text text--darken-2">
                {{ (item.estimated_cost || (item.real_price && item.real_price.sales_cost) || 0) | moneda }}
              </span>
              <span class="margin-pill mt-1" style="background: #f1f5f9; color: #475569;">
                {{ (item.estimated_margin || 0) | percent(1) }}
              </span>
            </div>
          </template>

          <template v-slot:item.real_price="{ item }">
            <div class="d-flex flex-column align-end py-2">
              <span class="body-2 font-weight-black" :class="marginClass(item.real_margin)">
                {{ (item.real_cost || (item.real_price && (typeof item.real_price === 'object' ? item.real_price.total : item.real_price)) || 0) | moneda }}
              </span>
              <span class="margin-pill mt-1" :class="marginClass(item.real_margin)">
                {{ (item.real_margin || 0) | percent(1) }} margen
              </span>
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    small
                    color="primary"
                    class="grey lighten-4"
                    @click="openDetail(item)"
                  >
                    <v-icon small>fas fa-eye</v-icon>
                  </v-btn>
                </template>
                <span>Ver desglose de costos</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <!-- Modals -->
    <v-dialog v-model="showDetail" fullscreen hide-overlay transition="dialog-bottom-transition" persistent>
      <detailed-post-cost
        v-if="showDetail && detailOtId"
        :work_order_id="detailOtId"
        @close="showDetail=false"
      />
    </v-dialog>

    <v-dialog v-model="display" max-width="1200" content-class="modern-dialog">
      <work-order-card :work_order='workOrderOnDisplay' @close="display=false"/>
    </v-dialog>
  </v-container>
</template>

<script>
import { fetchPostCostWorkOrders, fetchPostCostDashboard } from '@/api/reports'
import CustomerComplete2 from '@/components/customers/CustomerComplete2.vue'
import WorkOrderCard from '@/components/production/WorkOrderCard'
import ShowWorkOrder from '@/components/production/ShowWorkOrder'
import ExportPostCost from '@/components/reports/ExportPostCost'
import DetailedPostCost from '@/views/Reports/DetailedPostCost.vue'
import LineChart from '@/components/charts/LineChart.vue'
import DoughnutChart from '@/components/charts/DoughnutChart.vue'
import moment from 'moment'
import notifications from '@/utils/notifications'
import { generatePostCostDashboardPdf } from '@/doc_generators/postCostDashboardReport'
import businessLineFilter from '@/mixins/businessLineFilter'
const numeral = require('numeral')

export default {
  name: 'PostCostWorkOrders',
  mixins: [businessLineFilter],
  components: {
    CustomerComplete2,
    WorkOrderCard,
    ShowWorkOrder,
    ExportPostCost,
    DetailedPostCost,
    LineChart,
    DoughnutChart
  },
  data() {
    return {
      loading: false,
      loadingDashboard: false,
      downloadingPdf: false,
      error: '',
      asOf: null,
      period: 'current_month',
      dateMode: 'completed',
      periodOptions: [
        { text: 'Este mes', value: 'current_month' },
        { text: 'Mes anterior', value: 'last_month' },
        { text: 'Este trimestre', value: 'current_quarter' },
        { text: 'Este año', value: 'current_year' },
        { text: 'Personalizado', value: 'custom' }
      ],
      reposicionOptions: [
        { text: 'Normal', value: 'false' },
        { text: 'Reposición', value: 'true' }
      ],
      filter: {
        created_after: moment().startOf('month').format('YYYY-MM-DD'),
        created_before: moment().format('YYYY-MM-DD'),
        customer: '',
        seller: '',
        reposicion: '',
      },
      dash: {
        count: 0,
        sales: 0,
        estimated: 0,
        real: 0,
        profit: 0,
        variance: 0,
        real_margin: 0,
        estimated_margin: 0,
        buckets: {},
        monthly: [],
        customers: [],
        sellers: [],
        best: [],
        worst: [],
        insight: null,
        cost_structure: {},
        reprocess_ots: 0,
        overrun_ots: 0
      },
      lineData: { labels: [], datasets: [] },
      pieData: { labels: [], datasets: [{ data: [], backgroundColor: [] }] },
      showDetail: false,
      detailOtId: null,
      workOrderOnDisplay: {},
      options: {},
      totalWorkOrders: 0,
      display: false,
      search: '',
      workOrders: [],
      sellers: [],
      timer: null,
      headers: [
        { text: 'Orden de Trabajo', align: 'start', value: 'id', class: 'table-header', width: '150px' },
        { text: 'Fechas (In/Out)', align: 'start', value: 'created_at', class: 'table-header', width: '140px' },
        { text: 'Proyecto / Referencia', align: 'start', value: 'sales_note__name', class: 'table-header' },
        { text: 'Cliente / Vendedor', align: 'start', value: 'sales_note__customer__name', class: 'table-header' },
        { text: 'Precio Venta', align: 'end', value: 'total_sale', class: 'table-header', sortable: false, width: '140px' },
        { text: 'Costo Teórico', align: 'end', value: 'estimated_cost', class: 'table-header', sortable: false, width: '140px' },
        { text: 'Costo Real', align: 'end', value: 'real_price', class: 'table-header', sortable: false, width: '140px' },
        { text: '', align: 'center', value: 'actions', class: 'table-header', sortable: false, width: '60px' }
      ],
    }
  },
  computed: {
    buckets() {
      return this.dash.buckets || {}
    },
    totalMarginSales() {
      const b = this.buckets
      return Number((b.healthy && b.healthy.sales) || 0) +
             Number((b.ok && b.ok.sales) || 0) +
             Number((b.tight && b.tight.sales) || 0) +
             Number((b.loss && b.loss.sales) || 0)
    },
    marginHealthyPct() {
      return this.totalMarginSales > 0 ? (Number((this.buckets.healthy && this.buckets.healthy.sales) || 0) / this.totalMarginSales) * 100 : 0
    },
    marginOkPct() {
      return this.totalMarginSales > 0 ? (Number((this.buckets.ok && this.buckets.ok.sales) || 0) / this.totalMarginSales) * 100 : 0
    },
    marginTightPct() {
      return this.totalMarginSales > 0 ? (Number((this.buckets.tight && this.buckets.tight.sales) || 0) / this.totalMarginSales) * 100 : 0
    },
    marginLossPct() {
      return this.totalMarginSales > 0 ? (Number((this.buckets.loss && this.buckets.loss.sales) || 0) / this.totalMarginSales) * 100 : 0
    },
    formattedAsOf() {
      if (!this.asOf) return ''
      return moment(this.asOf).format('HH:mm')
    },
    pieHasData() {
      const dataset = (this.pieData.datasets && this.pieData.datasets[0]) || {}
      return (dataset.data || []).some((value) => Number(value) > 0)
    },
    maxCustomerSales() {
      return Math.max(1, ...((this.dash.customers || []).map((row) => row.sales || 0)))
    },
    maxSellerSales() {
      return Math.max(1, ...((this.dash.sellers || []).map((row) => row.sales || 0)))
    },
    periodLabel() {
      const map = {
        current_month: 'este mes',
        last_month: 'mes anterior',
        current_quarter: 'este trimestre',
        current_year: 'este año',
        custom: 'rango personalizado'
      }
      return map[this.period] || 'período seleccionado'
    },
    lineOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (item, data) => `${data.datasets[item.datasetIndex].label}: $${numeral(item.yLabel).format('0,0')}`
          }
        },
        scales: {
          xAxes: [{ gridLines: { display: false } }],
          yAxes: [{
            ticks: {
              callback: (value) => '$' + numeral(value).format('0,0a')
            },
            gridLines: { color: 'rgba(0,0,0,0.04)' }
          }]
        }
      }
    },
    pieOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'bottom', labels: { boxWidth: 10, fontStyle: 'bold', fontSize: 11 } },
        tooltips: {
          callbacks: {
            label: (item, data) => {
              const label = data.labels[item.index]
              const value = data.datasets[0].data[item.index]
              return `${label}: $${numeral(value).format('0,0')}`
            }
          }
        }
      }
    }
  },
  watch: {
    dateMode() {
      this.loadAll()
    },
    line() {
      this.loadAll()
    }
  },
  methods: {
    selectPeriod(val) {
      this.period = val
      this.onPeriodChange()
    },
    applyPeriodDates() {
      const today = moment()
      if (this.period === 'current_month') {
        this.filter.created_after = today.clone().startOf('month').format('YYYY-MM-DD')
        this.filter.created_before = today.format('YYYY-MM-DD')
      } else if (this.period === 'last_month') {
        const last = today.clone().subtract(1, 'month')
        this.filter.created_after = last.clone().startOf('month').format('YYYY-MM-DD')
        this.filter.created_before = last.clone().endOf('month').format('YYYY-MM-DD')
      } else if (this.period === 'current_quarter') {
        const month = today.month()
        this.filter.created_after = today.clone().month(Math.floor(month / 3) * 3).startOf('month').format('YYYY-MM-DD')
        this.filter.created_before = today.format('YYYY-MM-DD')
      } else if (this.period === 'current_year') {
        this.filter.created_after = today.clone().startOf('year').format('YYYY-MM-DD')
        this.filter.created_before = today.format('YYYY-MM-DD')
      }
    },
    onPeriodChange() {
      if (this.period === 'custom') return
      this.applyPeriodDates()
      this.loadAll()
    },
    onCustomDates() {
      this.period = 'custom'
      this.loadAll()
    },
    queryString() {
      let after = ''
      let before = ''
      if (this.filter.created_after) after = moment(this.filter.created_after).utc().format('YYYY-MM-DD HH:mm')
      if (this.filter.created_before) before = moment(this.filter.created_before).utc().add(1439, 'minutes').format('YYYY-MM-DD HH:mm')
      const customerId = this.filter.customer && this.filter.customer.id ? this.filter.customer.id : ''
      const dateParams = this.dateMode === 'completed'
        ? `completed_at_after=${after}&completed_at_before=${before}`
        : `created_after=${after}&created_before=${before}`
      return `${dateParams}&sales_note__customer=${customerId}&sales_note__customer__seller=${this.filter.seller || ''}&completed=true&sales_note__reposicion=${this.filter.reposicion || ''}&search=${this.search || ''}${this.lineQuery()}`
    },
    async loadAll() {
      this.options.page = 1
      await Promise.all([this.fetchDashboard(), this.fetch()])
    },
    async fetchDashboard() {
      this.loadingDashboard = true
      this.error = ''
      try {
        const data = await fetchPostCostDashboard(this.queryString())
        this.dash = Object.assign({
          buckets: {}, monthly: [], customers: [], sellers: [], best: [], worst: [], cost_structure: {}
        }, data)
        this.setLineBreakdown(data.lines)
        this.asOf = new Date()
        this.buildCharts()
      } catch (err) {
        this.error = 'No se pudo cargar el dashboard de postcosteo.'
        notifications.showError('Error cargando dashboard: ' + err)
      } finally {
        this.loadingDashboard = false
      }
    },
    buildCharts() {
      const months = this.dash.monthly || []
      this.lineData = {
        labels: months.map((row) => row.label),
        datasets: [
          {
            label: 'Venta',
            data: months.map((row) => row.sales),
            borderColor: '#0284c7',
            backgroundColor: 'rgba(2, 132, 199, 0.06)',
            borderWidth: 3,
            fill: true,
            lineTension: 0.35,
            pointRadius: 3
          },
          {
            label: 'Costo teórico',
            data: months.map((row) => row.estimated),
            borderColor: '#94a3b8',
            backgroundColor: 'transparent',
            borderWidth: 2,
            borderDash: [5, 5],
            fill: false,
            lineTension: 0.35,
            pointRadius: 2
          },
          {
            label: 'Costo real',
            data: months.map((row) => row.real),
            borderColor: '#0F172A',
            backgroundColor: 'transparent',
            borderWidth: 2,
            fill: false,
            lineTension: 0.35,
            pointRadius: 2
          }
        ]
      }
      const structure = this.dash.cost_structure || {}
      const slices = [
        { label: 'Procesos', value: structure.production_cost, color: '#0284c7' },
        { label: 'Reproceso', value: structure.reprocess_cost, color: '#f59e0b' },
        { label: 'Materiales', value: structure.consume_cost, color: '#0d9488' },
        { label: 'Mat. Reproceso', value: structure.consume_cost_reprocess, color: '#ea580c' },
        { label: 'Bodega', value: structure.warehouse_consumption, color: '#64748b' },
        { label: 'Adicionales', value: structure.additional_costs, color: '#7c3aed' },
        { label: 'Compras OT', value: structure.purchase_orders_costs, color: '#0f172a' }
      ].filter((slice) => Number(slice.value) > 0)
      this.pieData = {
        labels: slices.map((slice) => slice.label),
        datasets: [{
          data: slices.map((slice) => slice.value),
          backgroundColor: slices.map((slice) => slice.color)
        }]
      }
    },
    rankWidth(value, max) {
      return Math.max(8, Math.round((Number(value || 0) / Math.max(1, max)) * 100)) + '%'
    },
    marginClass(value) {
      if (value === null || value === undefined) return ''
      if (value < 0) return 'is-loss'
      if (value < 0.15) return 'is-warn'
      return 'is-ok'
    },
    async fetch() {
      this.loading = true
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      let ordering = '-id'
      if (sortBy && sortBy.length > 0) {
        ordering = sortDesc[0] ? sortBy[0] : '-' + sortBy[0]
      }
      const query = `ordering=${ordering}&page=${page || 1}&page_size=${itemsPerPage || 20}&${this.queryString()}`
      try {
        const data = await fetchPostCostWorkOrders(query)
        this.totalWorkOrders = data.count
        this.workOrders = (data.results || []).map(item => {
          const rp = item.real_price || {}
          const sales_cost = rp.sales_cost || 0
          const sales_total = rp.sales_total || 0
          const total = rp.total || 0
          item.estimated_margin = sales_total != 0 ? (1 - sales_cost / sales_total) : 0
          item.real_margin = sales_total != 0 ? (1 - total / sales_total) : 0
          return item
        })
      } catch (err) {
        notifications.showError('Error cargando OTs: ' + err)
      } finally {
        this.loading = false
      }
    },
    openDetail(item) {
      this.detailOtId = item.id
      this.showDetail = true
    },
    searchTimeOut() {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        this.loadAll()
      }, 800)
    },
    async downloadExecutivePdf() {
      if (this.downloadingPdf) return
      this.downloadingPdf = true
      try {
        let chartImageBase64 = null
        try {
          const chartEl = this.$refs.salesLineChart?.$el
          const canvas = chartEl?.tagName === 'CANVAS' ? chartEl : chartEl?.querySelector?.('canvas')
          if (canvas && typeof canvas.toDataURL === 'function') {
            chartImageBase64 = canvas.toDataURL('image/png', 2)
          }
        } catch (e) {
          console.warn('Could not extract chart canvas image:', e)
        }

        const company = this.$store.getters.getCompany || {}
        await generatePostCostDashboardPdf({
          company,
          startDate: this.filter.created_after,
          endDate: this.filter.created_before,
          dateMode: this.dateMode,
          periodLabel: this.periodLabel,
          lineName: this.selectedLineName,
          lines: this.lineBreakdown,
          serverTimezone: company.current_time_zone || 'America/Santiago',
          dash: this.dash,
          chartImageBase64
        })
        notifications.showSuccess('Reporte PDF ejecutivo de postcosteo generado correctamente.')
      } catch (err) {
        notifications.showError('No se pudo generar el reporte PDF: ' + (err.message || err))
      } finally {
        this.downloadingPdf = false
      }
    }
  },
  async mounted() {
    this.sellers = await this.$store.getters.getSellers
    this.applyPeriodDates()
    this.fetchDashboard()
  }
}
</script>

<style scoped>
.dashboard-container {
  background-color: #f8fafc;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
}
.border-bottom { border-bottom: 1px solid #e2e8f0 !important; }
.border-top { border-top: 1px solid #f1f5f9 !important; }
.shadow-xs { box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05) !important; }
.rounded-xl { border-radius: 12px !important; }
.tracking-tight { letter-spacing: -0.025em !important; }

/* Header & Date Mode */
.date-mode-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}
.mode-toggle {
  background: transparent;
}
.mode-toggle .v-btn {
  border: none !important;
  height: 32px !important;
}
.mode-toggle .v-btn--active {
  background: white !important;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08) !important;
  color: #0f172a !important;
}

/* Filter Card */
.filter-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}
.compact-date-input {
  max-width: 155px;
}
.compact-date-input >>> fieldset,
.compact-select >>> fieldset {
  border-radius: 8px !important;
  border-color: #e2e8f0 !important;
}

/* Hero KPIs */
.hero-kpi-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.hero-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}
.hero-kpi-card.is-primary {
  border-color: #bae6fd;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}
.kpi-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.kpi-value {
  font-size: 1.55rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.2;
}
.kpi-footer {
  border-top: 1px solid #f1f5f9;
}

/* Margin Distribution Track */
.margin-stacked-track {
  display: flex;
  height: 28px;
  border-radius: 8px;
  overflow: hidden;
  background: #f1f5f9;
}
.stacked-segment {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
  transition: filter 0.15s ease;
}
.stacked-segment:hover {
  filter: brightness(0.92);
}
.stacked-segment.is-healthy { background: #10b981; }
.stacked-segment.is-ok { background: #0284c7; }
.stacked-segment.is-tight { background: #f59e0b; }
.stacked-segment.is-loss { background: #e11d48; }

.segment-label {
  font-size: 11px;
  font-weight: 800;
  color: white;
  padding: 0 4px;
}

/* Margin Tier Cards */
.margin-tier-btn {
  padding: 12px 14px;
  border-radius: 10px;
  transition: transform 0.15s ease, border-color 0.15s ease;
}
.margin-tier-btn:hover {
  transform: translateY(-2px);
}
.margin-tier-btn.is-healthy {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
}
.margin-tier-btn.is-healthy .tier-name { color: #047857; }

.margin-tier-btn.is-ok {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
}
.margin-tier-btn.is-ok .tier-name { color: #0369a1; }

.margin-tier-btn.is-tight {
  background: #fffbeb;
  border: 1px solid #fde68a;
}
.margin-tier-btn.is-tight .tier-name { color: #b45309; }

.margin-tier-btn.is-loss {
  background: #fff1f2;
  border: 1px solid #fecdd3;
}
.margin-tier-btn.is-loss .tier-name { color: #be123c; }

.tier-name {
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.tier-count {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
}
.tier-sales {
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
  margin-top: 4px;
  letter-spacing: -0.02em;
}
.tier-share {
  font-size: 0.7rem;
  font-weight: 600;
  color: #64748b;
  margin-top: 2px;
}

/* Panels */
.panel-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 22px;
}
.panel-title {
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;
}
.panel-sub {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  margin-top: 2px;
}
.legend-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
}
.legend {
  display: flex;
  gap: 16px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}
.swatch {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}
.swatch.sold { background: #0284c7; }
.swatch.quoted { background: #94a3b8; }
.swatch.real { background: #0f172a; }

/* Rankings & Tracks */
.rank-row {
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  transition: background 0.15s ease;
}
.rank-row:hover {
  background: #f8fafc;
}
.rank-name {
  font-size: 13px;
  color: #0f172a;
}
.rank-meta {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}
.rank-val {
  font-size: 13px;
  color: #0f172a;
  letter-spacing: -0.01em;
}
.rank-track {
  height: 5px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
  margin-top: 4px;
}
.rank-fill {
  height: 100%;
  background: #0284c7;
  border-radius: 999px;
}
.rank-fill.is-teal {
  background: #0d9488;
}

/* OT List Buttons */
.ot-row {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  text-align: left;
  background: #fff;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 6px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.ot-row:hover {
  background: #fff1f2;
  border-color: #fecdd3;
}
.ot-row.is-best:hover {
  background: #ecfdf5;
  border-color: #a7f3d0;
}
.ot-id {
  font-size: 13px;
  font-weight: 800;
  color: #0f172a;
}
.ot-cust {
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  max-width: 220px;
}
.ot-meta {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  max-width: 300px;
}
.ot-nums {
  text-align: right;
  flex-shrink: 0;
}

/* Margin Pills & Status Colors */
.margin-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  display: inline-block;
}
.margin-pill.is-ok, .is-ok { color: #047857; background: #ecfdf5; }
.margin-pill.is-warn, .is-warn { color: #b45309; background: #fffbeb; }
.margin-pill.is-loss, .is-loss { color: #be123c; background: #fff1f2; }

/* Modern Card & Table */
.modern-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}
.modern-search-field {
  max-width: 320px;
}
.modern-search-field >>> fieldset {
  border-radius: 20px !important;
  border-color: #e2e8f0 !important;
}
.modern-table >>> thead th {
  background-color: #f8fafc !important;
  height: 48px !important;
  text-transform: uppercase;
  font-size: 0.72rem !important;
  letter-spacing: 0.05em;
  font-weight: 800 !important;
  color: #64748b !important;
  border-bottom: 1px solid #e2e8f0 !important;
}
.modern-table >>> td {
  height: 64px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}
.modern-table >>> tr:hover {
  background-color: #f8fafc !important;
}
.lh-1-2 { line-height: 1.2 !important; }
.empty-mini {
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
}

@media (max-width: 960px) {
  .modern-search-field { max-width: 100%; width: 100%; }
  .kpi-value { font-size: 1.35rem; }
}

.line-table >>> th {
  font-size: 11px !important;
  font-weight: 800 !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b !important;
}
.line-row { cursor: pointer; }
.is-selected-line {
  background: #f0fdf4;
  box-shadow: inset 3px 0 0 #00a650;
}
</style>
